import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Mail, Send } from 'lucide-react';
import { authService } from '../services/authService';
import { notify } from '../utils/notifications';

export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit() {
    if (!email.trim()) {
      notify.error('Please enter your email address');
      return;
    }
    setIsSubmitting(true);
    try {
      await authService.requestPasswordReset(email);
      setSubmitted(true);
    } catch {
      notify.error('Could not request a reset link', 'Please try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="size-full flex flex-col px-6" style={{ backgroundColor: 'var(--app-bg)' }}>
      <button
        type="button"
        aria-label="Back to sign in"
        onClick={() => navigate('/signin')}
        className="mt-6 -ml-2 size-11 rounded-full flex items-center justify-center active:opacity-70"
        style={{ color: 'var(--app-text)' }}
      >
        <ArrowLeft className="w-6 h-6" />
      </button>

      <div className="flex-1 flex flex-col justify-center pb-20">
        <div className="rounded-2xl size-16 flex items-center justify-center mb-6 border" style={{ backgroundColor: 'var(--app-accent-dim)', borderColor: 'var(--app-accent-border)' }}>
          <Mail className="w-8 h-8" style={{ color: 'var(--app-accent)' }} />
        </div>
        <h1 className="text-[30px] tracking-tight" style={{ color: 'var(--app-text)' }}>Reset password</h1>
        <p className="text-base mt-3 leading-6" style={{ color: 'var(--app-text-muted)' }}>
          Enter your email and we’ll send a link to create a new password.
        </p>

        {submitted ? (
          <div className="mt-8 rounded-xl p-4 border" style={{ backgroundColor: 'var(--app-card-bg)', borderColor: 'var(--app-border)' }}>
            <p className="text-base" style={{ color: 'var(--app-text)' }}>If an account exists for that email, a password reset link is on its way.</p>
            <p className="text-sm mt-2" style={{ color: 'var(--app-text-muted)' }}>Check your inbox and spam folder, then return here using the link.</p>
            <button onClick={() => navigate('/signin')} className="mt-5 h-12 w-full rounded-xl text-base text-white active:scale-[0.98]" style={{ backgroundColor: 'var(--app-accent-solid)' }}>
              Back to sign in
            </button>
          </div>
        ) : (
          <div className="mt-8 flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <label htmlFor="reset-email" className="text-base" style={{ color: 'var(--app-text)' }}>Email address</label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@example.com"
                className="h-14 rounded-xl px-4 text-base outline-none"
                style={{ backgroundColor: 'var(--app-input-bg)', border: '1px solid var(--app-input-border)', color: 'var(--app-text)' }}
              />
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="h-14 rounded-xl text-white text-lg flex items-center justify-center gap-2 disabled:opacity-70 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--app-accent-solid)' }}
            >
              {isSubmitting ? 'Sending…' : <><Send className="w-5 h-5" /> Send reset link</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
